import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../../i18n";
import api from "../../../lib/api";
import { Button, Input } from "../../../components/ui";

// S58: Die Seite hat zwei Modi —
//   1. Anfordern (ohne ?token=): E-Mail eingeben, Link kommt per Mail
//   2. Bestätigen (mit ?token=…): neues Passwort setzen
const PasswordResetPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const isConfirmMode = Boolean(token);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  const handleRequest = async () => {
    if (!email) return;
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);
    try {
      await api.post("/auth/password-reset", { email });

      setSuccessMessage(t("auth.passwordReset.successSent"));
      setEmail("");
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setErrorMessage(
        err.response?.data?.error || err.message || t("auth.passwordReset.errorGeneric"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!password || password.length < 12) {
      setErrorMessage(t("auth.passwordReset.errorPasswordTooShort"));
      return;
    }
    if (password !== passwordRepeat) {
      setErrorMessage(t("auth.passwordReset.errorPasswordMismatch"));
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, password });
      setSuccessMessage(t("auth.passwordReset.successConfirmed"));
      setTimeout(() => navigate("/login"), 1800);
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      setErrorMessage(
        err.response?.data?.error || err.message || t("auth.passwordReset.errorGeneric"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = isConfirmMode ? handleConfirm : handleRequest;
  const submitDisabled = isLoading || (isConfirmMode ? !password || !passwordRepeat : !email);

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-start lg:items-center justify-center bg-surface-nav text-on-surface px-6 py-12"
      style={{
        backgroundImage:
          "radial-gradient(700px 400px at 50% 0%, rgb(var(--primary) / 0.07), transparent 60%)",
      }}
    >
      {/* Formular-Karte (einspaltig, Stil wie Login 7b) */}
      <div className="w-full max-w-[420px] bg-surface-container border border-outline-variant rounded-xl p-6 sm:p-8">
        <div className="w-11 h-11 rounded-[10px] bg-primary/10 grid place-items-center mb-5">
          <span className="material-symbols-outlined text-primary text-xl leading-none">
            lock_reset
          </span>
        </div>
        <h1 className="text-2xl font-semibold tracking-[-.015em] text-on-surface">
          {isConfirmMode ? t("auth.passwordReset.confirmTitle") : t("auth.passwordReset.title")}
        </h1>
        <p className="text-[13px] leading-relaxed text-on-surface-muted mt-1.5">
          {isConfirmMode
            ? t("auth.passwordReset.confirmDescription")
            : t("auth.passwordReset.description")}
        </p>

        {/* Meldungen */}
        {errorMessage && (
          <div
            className="flex items-center gap-3 p-3.5 mt-6 rounded-lg bg-error/10 border border-error/20"
            role="alert"
          >
            <span className="material-symbols-outlined text-error text-xl leading-none">error</span>
            <p className="text-[13px] text-error">{errorMessage}</p>
          </div>
        )}
        {successMessage && (
          <div
            className="flex items-center gap-3 p-3.5 mt-6 rounded-lg bg-success/10 border border-success/20"
            role="status"
          >
            <span className="material-symbols-outlined text-success text-xl leading-none">
              check_circle
            </span>
            <p className="text-[13px] text-success">{successMessage}</p>
          </div>
        )}

        {/* Formular */}
        <form
          className="mt-7 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {isConfirmMode ? (
            <>
              <Input
                id="reset-password"
                ref={firstInputRef}
                type="password"
                label={t("auth.passwordReset.newPasswordLabel")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <Input
                id="reset-password-repeat"
                type="password"
                label={t("auth.passwordReset.newPasswordRepeatLabel")}
                value={passwordRepeat}
                onChange={(e) => setPasswordRepeat(e.target.value)}
                placeholder="••••••••"
              />
            </>
          ) : (
            <Input
              id="reset-email"
              ref={firstInputRef}
              type="email"
              label={t("auth.common.emailLabel")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.common.emailPlaceholder")}
            />
          )}

          <Button type="submit" size="lg" fullWidth disabled={submitDisabled} className="!mt-6">
            {isLoading
              ? t("auth.passwordReset.submitInProgress")
              : isConfirmMode
                ? t("auth.passwordReset.confirmSubmit")
                : t("auth.passwordReset.submit")}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[13px] text-on-surface-muted hover:text-on-surface transition-colors duration-150"
          >
            <span className="material-symbols-outlined text-base leading-none">arrow_back</span>
            {t("auth.passwordReset.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
